import InfoChit from '@/components/chit/InfoChit';
import { Button } from '@/components/form/Button';
import { Form } from '@/components/form/Form';
import { ButtonVariant } from '@/components/form/buttonHelper';
import DaaSTable, { DaasTableColumn } from '@/components/table/DaasTable';
import { SortDirection, sort } from '@/components/table/sortHelper';
import { StockLevel } from '@/services/api/contracts/stock/stockLevels';
import { useUpsertReservedStock } from '@/services/api/hooks/stock/reservedStock';
import { RoleType, useRole } from '@/services/authentication/hooks/user';
import { StockBinLevel } from '@/services/catalogue/stockLevels/useDefaultBundleStockLevels';
import { StockLevelStatus, useStockLevelStatus } from '@/services/catalogue/useCustomerCatalogueService';
import { zodResolver } from '@hookform/resolvers/zod';
import { FunctionComponent, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router';
import { z } from 'zod';
import { StockTableData } from '../stockLevel.types';
import { SummaryStockLevelRow } from './SummaryStockLevelRow';
import SummaryStockLevelsUnsavedChanges from './SummaryStockLevelsUnsavedChanges';

const StockTableFields = z.object({
  stockData: z.array(StockTableData)
});

type StockTableFields = z.infer<typeof StockTableFields>;

interface Props {
  stockLevels: StockBinLevel[];
}

export const SummaryStockLevelsTable: FunctionComponent<Props> = ({ stockLevels }) => {
  const tableHeaders: DaasTableColumn[] = [
    {
      accessor: 'deviceName',
      sortable: true,
      text: 'Device Name',
      size: '1fr'
    },
    {
      accessor: 'sku',
      sortable: true,
      text: 'SKU',
      size: '0.4fr'
    },
    {
      accessor: 'inStock',
      sortable: true,
      text: 'In Stock',
      size: '0.4fr'
    },
    {
      accessor: 'minStockLevel',
      sortable: true,
      text: 'Min Stock Level',
      size: '0.4fr'
    },
    {
      accessor: 'maxStockLevel',
      sortable: true,
      text: 'Max Stock Level',
      size: '0.4fr'
    }
  ];

  const [sortColumnId, setSortColumnId] = useState<string>(tableHeaders[0].accessor);
  const [sortDirection, setSortDirection] = useState<SortDirection>(SortDirection.DESC);

  const processStockBinLevels = (stockBinLevels: StockBinLevel[]) => {
    return stockBinLevels.map(s => ({
      productId: s.product.id,
      deviceName: s.product.name,
      sku: s.product.productId,
      inStock: s.reservedStock ?? 0,
      minStockLevel: s.minStockLevel,
      maxStockLevel: s.maxStockLevel,
      recMinStockLevel: s.recMinStockLevel,
      recMaxStockLevel: s.recMaxStockLevel
    }));
  };

  const initialTableData: StockTableData[] = useMemo(() => {
    const data = processStockBinLevels(stockLevels);
    const sorted = sort(data, sortColumnId, sortDirection);

    return sorted;
  }, [stockLevels, sortColumnId, sortDirection]);

  const methods = useForm<StockTableFields>({
    resolver: zodResolver(StockLevel),
    defaultValues: { stockData: initialTableData }
  });

  const data = methods.watch('stockData');

  const liveSortedTableData: StockTableData[] = useMemo(() => {
    const sorted = sort(data, sortColumnId, sortDirection);

    methods.setValue('stockData', sorted);

    return sorted;
  }, [sortColumnId, sortDirection]);

  const { partnerId, companyId } = useParams();
  if (!(partnerId && companyId)) throw new Error('Missing params');

  const save = useUpsertReservedStock(partnerId, companyId);

  const [unsavedChanges, setUnsavedChanges] = useState(false);

  const onSave = async () => {
    if (!data) return;

    await save.mutateAsync(
      data.map(row => {
        return {
          daasCatalogItemId: row.productId,
          stockLevel: row.inStock ?? 0
        };
      })
    );

    setUnsavedChanges(false);
  };

  const status = useStockLevelStatus();

  const readOnly = useRole().type !== RoleType.Admin;

  return (
    <>
      <SummaryStockLevelsUnsavedChanges unsavedChanges={unsavedChanges} />
      <Form className="space-y-3" methods={methods} onSubmit={onSave}>
        <div className="flex justify-between">
          <div className="flex gap-2 items-center">
            <div className="font-bold">Current Stock Level Status:</div>
            <InfoChit type={status === StockLevelStatus.APPROVED ? 'blue' : 'orange'}>
              {status === StockLevelStatus.APPROVED ? 'Approved' : 'Draft'}
            </InfoChit>
          </div>

          <Button variant={ButtonVariant.Primary} onClick={onSave} loading={save.isLoading}>
            <span>Save</span>
          </Button>
        </div>

        <DaaSTable
          headers={tableHeaders}
          onSortCallback={(columnId, direction) => {
            setSortColumnId(columnId);
            setSortDirection(direction);
          }}
          sortColumnId={sortColumnId}
          sortDirection={sortDirection}
        >
          {liveSortedTableData.map((t, index) => (
            <SummaryStockLevelRow
              data={t}
              key={`${t.productId}-${indexedDB}`}
              index={index}
              readonly={readOnly}
              onReservedStockChange={() => {
                setUnsavedChanges(true);
              }}
            />
          ))}
        </DaaSTable>
      </Form>
    </>
  );
};
